import { Box, Button, Divider, Modal, Paper } from "@mui/material";
import React from "react";
import { useAppSelector } from "../../../app/hooks";
import { selectLabels } from "../../../slices/language";
import IconifyIcon from "../../IconifyIcon";
import IdExample from "../../../assets/id-min.png";
import PlateExample from "../../../assets/plate-min.png";
import PortraitExample from "../../../assets/person-min.png";
import EquipmentExample from "../../../assets/computer-min.png";

interface PhotoModalProps {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  photoType: "id" | "plates" | "visitor" | "other";
  setFile: (file: File | null) => void;
}

const PhotoModal = ({
  showModal,
  setShowModal,
  photoType,
  setFile,
}: PhotoModalProps) => {
  const labels = useAppSelector(selectLabels);

  const [photo, setPhoto] = React.useState<string | null>(null);
  const [stream, setStream] = React.useState<MediaStream | null>(null);

  const videoRef = React.useRef<HTMLVideoElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  // Function to start the camera
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: true,
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        // Wait for metadata to load before playing
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play();
        };
      }
    } catch (error) {
      console.error("Error accessing the camera:", error);
    }
  };

  // Stop the camera
  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  };

  // Initialize the camera when the modal opens
  React.useEffect(() => {
    if (showModal) {
      startCamera(); // Solo inicia la cámara si no hay una foto
    } else if (!showModal) {
      stopCamera(); // Detén la cámara si se cierra el modal
    }

    return () => {
      stopCamera(); // Asegúrate de detener la cámara al limpiar
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  // Function to take a photo and stop the camera
  const takePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      if (context) {
        context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
        const photoData = canvas.toDataURL("image/png");
        setPhoto(photoData); // Save the photo as Data URL
      }
    }
    // stopCamera();
  };

  // Reset photo and restart the camera
  const retakePhoto = () => {
    setPhoto(null);
    startCamera();
  };

  const onSaveFile = async () => {
    if (photo) {
      const imageSrc = photo;
      setPhoto(imageSrc);
      const file = await dataURLtoFile(imageSrc, "captured_image.jpeg");
      setFile(file);
      setShowModal(false);
      stopCamera();
    }
  };

  const dataURLtoFile = async (dataurl: any, filename: string) => {
    const res = await fetch(dataurl);
    const blob = await res.blob();

    return new File([blob], filename, { type: blob.type });
  };

  return (
    <Modal open={showModal} onClose={() => setShowModal(false)}>
      <Box
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          style={{
            maxWidth: 800,
            minWidth: 350,
            width: "100%",
            boxSizing: "border-box",
            paddingTop: 20,
            paddingBottom: 20,
            paddingRight: 30,
            paddingLeft: 30,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div className="w-100 flex align-items-center justify-content-between">
            <p style={{ textAlign: "center" }}>
              {labels.photo_capture}: {photoType === "id" && labels.id}
              {photoType === "plates" && labels.plates}
              {photoType === "visitor" && labels.portrait}
              {photoType === "other" && labels.equipment}
            </p>
            <IconifyIcon
              icon={"material-symbols:close-rounded"}
              onClick={() => {
                setShowModal(false);
                setPhoto(null);
              }}
              className="pointer font-24"
            />
          </div>
          <Divider className="w-100 mt-6" />
          <div className="w-100 flex">
            <div className="w-70">
              {/* {photo ? (
                <img
                  src={photo}
                  alt="Captured"
                  style={{ width: "100%", marginTop: 10 }}
                />
              ) : (
                <video
                  ref={videoRef}
                  style={{ width: "100%", marginTop: 10 }}
                ></video>
              )} */}
              <div className="w-100">
                <video
                  ref={videoRef}
                  style={{ width: "100%", marginTop: 10 }}
                ></video>
                <canvas
                  ref={canvasRef}
                  style={{ display: "none" }}
                  width="640"
                  height="480"
                ></canvas>
              </div>
              <div className="w-100 flex justify-content-center">
                <Button
                  variant="contained"
                  onClick={takePhoto}
                  style={{ marginTop: 20 }}
                >
                  {"Capturar foto"}
                </Button>
              </div>
            </div>
            <div className="w-30 pl-10 box-sizing flex flex-dir-col justify-content-between">
              <div className="w-100 mt-1">
                <div
                  className="w-100 p-6 border-box mt-4"
                  style={{ height: 140, backgroundColor: "black" }}
                >
                  {photoType === "id" && (
                    <img
                      src={IdExample}
                      alt="ID example"
                      width={"100%"}
                      height={"100%"}
                      style={{ objectFit: "contain" }}
                    />
                  )}
                  {photoType === "plates" && (
                    <img
                      src={PlateExample}
                      alt="ID example"
                      width={"100%"}
                      height={"100%"}
                      style={{ objectFit: "contain" }}
                    />
                  )}
                  {photoType === "other" && (
                    <img
                      src={EquipmentExample}
                      alt="ID example"
                      width={"100%"}
                      height={"100%"}
                      style={{ objectFit: "contain" }}
                    />
                  )}
                  {photoType === "visitor" && (
                    <img
                      src={PortraitExample}
                      alt="ID example"
                      width={"100%"}
                      height={"100%"}
                      style={{ objectFit: "contain" }}
                    />
                  )}
                </div>
              </div>
              {photo && (
                <div className="w-100">
                  <img
                    src={photo}
                    alt="Captured"
                    style={{ width: "100%", marginTop: 10 }}
                  />
                  <div className="w-100 flex justify-content-center">
                    <Button
                      variant="contained"
                      onClick={onSaveFile}
                      style={{ marginTop: 20 }}
                    >
                      {"Guardar foto"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Paper>
      </Box>
    </Modal>
  );
};

export default PhotoModal;
